/**
 * @file CategoriesPanel.jsx
 * @brief Renders the category tree administration panel inside the catalog workspace.
 */
import styles from "../catalog-admin.module.css";
import { clampToMaxLength, FIELD_LIMITS } from "../../../constants/fieldLimits";
import { buildCategoryPath, formatCategoryOptionLabel, slugifyValue } from "../utils";
import useCatalogCategories from "../../../hooks/useCatalogCategories";
import { PaginationControls, SectionHeader } from "./shared";


function CategoryNode({
    node,
    depth,
    categoryById,
    childrenByParent,
    expandedIds,
    loadingIds,
    onToggle,
    onLoadMore,
    editingId,
    editDraft,
    setEditDraft,
    parentOptions,
    blockedParentIds,
    saving,
    onSave,
    onStartEdit,
    onCancelEdit,
    onDelete,
    t,
}) {
    const isExpanded = expandedIds.has(node.id);
    const isEditing = editingId === node.id;
    const childState = childrenByParent[node.id] || { items: [], next: null, loaded: false };
    const children = childState.items || [];
    const hasChildren = (node.child_count ?? 0) > 0;
    const isLoading = loadingIds.has(node.id);
    const categoryPath = buildCategoryPath(node, categoryById);

    return (
        <li className={styles.treeNode}>
            <div className={styles.treeRow} style={{ paddingLeft: `${depth * 20 + 14}px` }}>
                <button
                    className={styles.chevronButton}
                    disabled={!hasChildren && !isExpanded}
                    onClick={() => onToggle(node)}
                    type="button"
                >
                    {!hasChildren ? "•" : isExpanded ? "▾" : "▸"}
                </button>

                {isEditing ? (
                    <div className={styles.editorCard}>
                        <input
                            className={styles.input}
                            value={editDraft.name}
                            onChange={(event) => setEditDraft((current) => ({ ...current, name: clampToMaxLength(event.target.value, FIELD_LIMITS.categoryName) }))}
                            placeholder={t("catalogAdmin.namePlaceholder")}
                            maxLength={FIELD_LIMITS.categoryName}
                        />
                        <input
                            className={styles.input}
                            value={editDraft.slug}
                            onChange={(event) => setEditDraft((current) => ({ ...current, slug: clampToMaxLength(event.target.value, FIELD_LIMITS.categorySlug) }))}
                            placeholder={t("catalogAdmin.slugPlaceholder")}
                            maxLength={FIELD_LIMITS.categorySlug}
                        />
                        <select
                            className={styles.select}
                            value={editDraft.parent}
                            onChange={(event) => setEditDraft((current) => ({ ...current, parent: event.target.value }))}
                        >
                            <option value="">{t("catalogAdmin.noParent")}</option>
                            {parentOptions
                                .filter((option) => !blockedParentIds.has(option.id))
                                .map((option) => (
                                    <option key={option.id} value={option.id}>{option.name}</option>
                                ))}
                        </select>
                        <div className={styles.rowActions}>
                            <button className={styles.successButton} disabled={saving} onClick={() => onSave(node.id)} type="button">
                                {t("catalogAdmin.save")}
                            </button>
                            <button className={styles.ghostButton} onClick={onCancelEdit} type="button">
                                {t("catalogAdmin.cancel")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className={styles.treeContent}>
                            <div className={styles.treeTitleRow}>
                                <p className={styles.rowTitle}>{node.name}</p>
                                {node.parent_name ? <span className={styles.parentBadge}>{node.parent_name}</span> : <span className={styles.rootBadge}>{t("catalogAdmin.topLevel")}</span>}
                            </div>
                            <p className={styles.rowMeta}>{node.slug}</p>
                            {categoryPath && <p className={styles.pathMeta}>{t("catalogAdmin.path")}: {categoryPath}</p>}
                        </div>
                        <div className={styles.rowActions}>
                            <span className={styles.childCount}>{t("catalogAdmin.childCount", { count: node.child_count ?? 0 })}</span>
                            <button className={styles.iconButton} onClick={() => onStartEdit(node)} type="button">
                                {t("catalogAdmin.edit")}
                            </button>
                            <button className={styles.dangerButton} onClick={() => onDelete(node.id)} type="button">
                                {t("catalogAdmin.delete")}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {isExpanded && hasChildren && (
                <div className={styles.branchBody}>
                    {isLoading && children.length === 0 ? (
                        <div className={styles.skeletonStack}>
                            {[...Array(Math.min(3, node.child_count || 1))].map((_, index) => (
                                <div key={index} className={styles.skeletonRow} />
                            ))}
                        </div>
                    ) : (
                        <>
                            <ul className={styles.treeList}>
                                {children.map((child) => (
                                    <CategoryNode
                                        key={child.id}
                                        node={child}
                                        depth={depth + 1}
                                        categoryById={categoryById}
                                        childrenByParent={childrenByParent}
                                        expandedIds={expandedIds}
                                        loadingIds={loadingIds}
                                        onToggle={onToggle}
                                        onLoadMore={onLoadMore}
                                        editingId={editingId}
                                        editDraft={editDraft}
                                        setEditDraft={setEditDraft}
                                        parentOptions={parentOptions}
                                        blockedParentIds={blockedParentIds}
                                        saving={saving}
                                        onSave={onSave}
                                        onStartEdit={onStartEdit}
                                        onCancelEdit={onCancelEdit}
                                        onDelete={onDelete}
                                        t={t}
                                    />
                                ))}
                            </ul>
                            {childState.next && (
                                <button className={styles.loadMoreButton} disabled={isLoading} onClick={() => onLoadMore(node.id)} type="button">
                                    {isLoading ? t("catalogAdmin.loadingChildren") : t("catalogAdmin.loadMoreChildren")}
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}
        </li>
    );
}


/**
 * Render the CRUD interface for hierarchical listing categories.
 * @param {Object} props Component props.
 * @param {Object} props.api API client used for category operations.
 * @param {Function} props.t Translation helper.
 * @returns {JSX.Element} The categories admin panel.
 */
export default function CategoriesPanel({ api, t }) {
    const {
        roots,
        rootCount,
        rootPage,
        rootNext,
        rootPrevious,
        rootSearch,
        setRootSearch,
        childrenByParent,
        expandedIds,
        loadingRoots,
        loadingIds,
        name,
        setName,
        slug,
        setSlug,
        parentSearch,
        setParentSearch,
        parentCandidates,
        selectedParent,
        setSelectedParent,
        parentLookupLoading,
        editingId,
        setEditingId,
        editDraft,
        setEditDraft,
        saving,
        error,
        parentOptions,
        categoryById,
        loadRoots,
        loadChildren,
        handleToggle,
        handleAdd,
        handleSave,
        handleDelete,
        clearParentSelection,
        selectParentCandidate,
        startEdit,
    } = useCatalogCategories({ api, t });

    /**
        * Collect the currently loaded descendants of a category to block invalid parent selections.
        * @param {string} categoryId Category identifier used as the traversal root.
        * @returns {Set<string>} Loaded descendant identifiers including the root category.
     */
    function getLoadedDescendantIds(categoryId) {
        const descendants = new Set([categoryId]);
        const stack = [categoryId];

        while (stack.length > 0) {
            const currentId = stack.pop();
            const children = childrenByParent[currentId]?.items || [];
            children.forEach((child) => {
                if (!descendants.has(child.id)) {
                    descendants.add(child.id);
                    stack.push(child.id);
                }
            });
        }
        return descendants;
    }

    return (
        <section className={`${styles.panel} ${styles.categoryPanel}`}>
            <SectionHeader
                icon="🏷"
                title={t("catalogAdmin.categories")}
                subtitle={t("catalogAdmin.categoriesSubtitle")}
                actions={
                    <button className={styles.ghostButton} onClick={() => loadRoots(rootPage, rootSearch)} type="button">
                        {t("catalogAdmin.refresh")}
                    </button>
                }
            />

            <form className={styles.composer} onSubmit={handleAdd}>
                <input
                    className={styles.input}
                    placeholder={t("catalogAdmin.namePlaceholder")}
                    value={name}
                    onChange={(event) => {
                        const nextValue = clampToMaxLength(event.target.value, FIELD_LIMITS.categoryName);
                        setName(nextValue);
                        setSlug((current) => (current === "" || current === slugifyValue(name) ? clampToMaxLength(slugifyValue(nextValue), FIELD_LIMITS.categorySlug) : current));
                    }}
                    maxLength={FIELD_LIMITS.categoryName}
                />
                <input
                    className={styles.input}
                    placeholder={t("catalogAdmin.slugPlaceholder")}
                    value={slug}
                    onChange={(event) => setSlug(clampToMaxLength(event.target.value, FIELD_LIMITS.categorySlug))}
                    maxLength={FIELD_LIMITS.categorySlug}
                />
                <div className={styles.asyncParentField}>
                    <input
                        className={styles.input}
                        placeholder={t("catalogAdmin.searchParentPlaceholder")}
                        value={parentSearch}
                        onChange={(event) => {
                            const next = event.target.value;
                            setParentSearch(next);
                            if (selectedParent && next !== formatCategoryOptionLabel(selectedParent)) {
                                setSelectedParent(null);
                            }
                        }}
                    />
                    <div className={styles.inlineToolbarGroup}>
                        {selectedParent ? (
                            <span className={styles.inlineMeta}>{t("catalogAdmin.parentSelected", { name: formatCategoryOptionLabel(selectedParent) })}</span>
                        ) : (
                            <span className={styles.inlineMeta}>{t("catalogAdmin.noParentTopLevel")}</span>
                        )}
                        <button
                            className={styles.ghostButton}
                            onClick={clearParentSelection}
                            type="button"
                        >
                            {t("catalogAdmin.clearParent")}
                        </button>
                    </div>
                    {parentSearch.trim() && (
                        <div className={styles.asyncParentResults}>
                            {parentLookupLoading ? (
                                <p className={styles.inlineMeta}>{t("catalogAdmin.searchingParents")}</p>
                            ) : parentCandidates.length === 0 ? (
                                <p className={styles.inlineMeta}>{t("catalogAdmin.noParentResults")}</p>
                            ) : (
                                <ul className={styles.parentResultList}>
                                    {parentCandidates.map((candidate) => (
                                        <li key={candidate.id}>
                                            <button
                                                className={styles.parentResultButton}
                                                onClick={() => selectParentCandidate(candidate)}
                                                type="button"
                                            >
                                                {formatCategoryOptionLabel(candidate)}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <button className={styles.primaryButton} disabled={saving || !name.trim() || !slug.trim()} type="submit">
                    {saving ? t("catalogAdmin.saving") : t("catalogAdmin.addCategory")}
                </button>
            </form>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    placeholder={t("catalogAdmin.searchAllCategories")}
                    value={rootSearch}
                    onChange={(event) => setRootSearch(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            loadRoots(1, event.currentTarget.value);
                        }
                    }}
                />
                <div className={styles.inlineToolbarGroup}>
                    <button className={styles.ghostButton} onClick={() => loadRoots(1, rootSearch)} type="button">
                        {t("catalogAdmin.search")}
                    </button>
                    <span className={styles.inlineMeta}>{t("catalogAdmin.resultCount", { count: rootCount })}</span>
                </div>
            </div>

            {error && <p className={styles.errorMsg}>{error}</p>}

            {loadingRoots ? (
                <div className={styles.skeletonStack}>
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className={styles.skeletonRow} />
                    ))}
                </div>
            ) : roots.length === 0 ? (
                <p className={styles.empty}>{t("catalogAdmin.noCategories")}</p>
            ) : (
                <>
                    <ul className={styles.treeList}>
                        {roots.map((node) => (
                            <CategoryNode
                                key={node.id}
                                node={node}
                                depth={0}
                                categoryById={categoryById}
                                childrenByParent={childrenByParent}
                                expandedIds={expandedIds}
                                loadingIds={loadingIds}
                                onToggle={handleToggle}
                                onLoadMore={(parentIdToLoad) => {
                                    const branch = childrenByParent[parentIdToLoad];
                                    if (!branch?.next) {
                                        return;
                                    }
                                    loadChildren(parentIdToLoad, (branch.page || 1) + 1, true);
                                }}
                                editingId={editingId}
                                editDraft={editDraft}
                                setEditDraft={setEditDraft}
                                parentOptions={parentOptions}
                                blockedParentIds={editingId === node.id ? getLoadedDescendantIds(node.id) : new Set([node.id])}
                                saving={saving}
                                onSave={handleSave}
                                onStartEdit={startEdit}
                                onCancelEdit={() => setEditingId(null)}
                                onDelete={handleDelete}
                                t={t}
                            />
                        ))}
                    </ul>

                    <PaginationControls
                        previous={rootPrevious}
                        next={rootNext}
                        disabled={loadingRoots}
                        onPrevious={() => loadRoots(Math.max(1, rootPage - 1), rootSearch)}
                        onNext={() => loadRoots(rootPage + 1, rootSearch)}
                        t={t}
                    />
                </>
            )}
        </section>
    );
}
